import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMount } from 'react-use';
import { Button } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import classNames from 'classnames';

import { SL_WEB_CAM_PAYWALL_REDESIGN } from 'common/treatments';
import { useTreatments } from 'utils/treatments';
import CamListItem from '../CamListItem';
import { showPremiumCamCTA } from '../../utils/premiumCam';
import { SurfHeight } from '../../types/units';
import { SpotReportData } from '../../types/spot';
import { useAppDispatch, useAppSelector } from '../../stores/hooks';
import { getSpotCameras } from '../../selectors/spot';
import { setCamListScrollPosition } from '../../actions/animations';

import type { Camera, CamListScrollPosition } from '../../types/camera';

import styles from './CamList.module.scss';

type SpotRefs = Record<string, HTMLDivElement | null>;
interface Props {
  spotId: string;
  scrollLeft?: number;
  units: SurfHeight;
  hasPremiumCamPermissions?: boolean;
  spots: SpotReportData[];
}

const CamList: React.FC<Props> = ({
  spotId,
  scrollLeft,
  units,
  hasPremiumCamPermissions = false,
  spots,
}) => {
  const camListRef = useRef<HTMLDivElement>(null);
  const spotRefs = useRef<SpotRefs>({});
  const cameras: Camera[] = useAppSelector(getSpotCameras);
  const hasCamAngles = cameras.length > 1;

  const treatments = useTreatments();
  const paywallRedesign = treatments[SL_WEB_CAM_PAYWALL_REDESIGN];

  const dispatch = useAppDispatch();

  const doSetCamListScrollPosition = useCallback(
    (scrollPosition: CamListScrollPosition) => dispatch(setCamListScrollPosition(scrollPosition)),
    [dispatch],
  );

  const getOffSetCenter = useCallback((spotRef: HTMLDivElement) => {
    if (camListRef.current) {
      const itemWidth = spotRef.offsetWidth;

      return camListRef.current.clientWidth / 2 - itemWidth;
    }
    return 0;
  }, []);

  const centerListItem = useCallback(() => {
    const currentSpotRef = spotRefs.current[spotId];
    if (currentSpotRef) {
      const offSetAtSpot = currentSpotRef.offsetLeft;
      const offSetCenter = getOffSetCenter(currentSpotRef);

      // add half the margin between elements to center properly
      const left = offSetAtSpot - offSetCenter + 7.5;

      doSetCamListScrollPosition({ top: 0, left });
    }
  }, [doSetCamListScrollPosition, getOffSetCenter, spotId]);

  const doScrollLeft = useCallback(() => {
    if (camListRef.current) {
      const left = camListRef.current.scrollLeft - camListRef.current.clientWidth;
      doSetCamListScrollPosition({ top: 0, left });
    }
  }, [doSetCamListScrollPosition]);

  const doScrollRight = useCallback(() => {
    if (camListRef.current) {
      const left = camListRef.current.scrollLeft + camListRef.current.clientWidth;
      doSetCamListScrollPosition({ top: 0, left });
    }
  }, [doSetCamListScrollPosition]);

  useMount(centerListItem);

  useEffect(centerListItem, [spotId, centerListItem]);

  useEffect(() => {
    if (camListRef.current && scrollLeft) {
      camListRef.current.scrollLeft = scrollLeft;
    }
  }, [scrollLeft]);

  const isEndOfScroll = useMemo(() => {
    if (camListRef.current && scrollLeft) {
      return scrollLeft + camListRef.current.offsetWidth >= camListRef.current.scrollWidth;
    }
    return false;
  }, [scrollLeft]);

  const isStartOfScroll = !scrollLeft || scrollLeft <= 0;

  return (
    <div className={styles.camList} data-testid="cam-list">
      <div
        className={classNames(
          styles.camListArrowButtons,
          hasCamAngles && styles.arrowButtonsMultiCam,
        )}
      >
        <Button
          variant="secondary"
          color="dark"
          aria-label="previous"
          onClick={() => doScrollLeft()}
          disabled={isStartOfScroll}
        >
          <ChevronLeftRoundedIcon />
        </Button>
        <Button
          variant="secondary"
          color="dark"
          aria-label="next"
          onClick={() => doScrollRight()}
          disabled={isEndOfScroll}
        >
          <ChevronRightRoundedIcon />
        </Button>
      </div>
      <div ref={camListRef} className={styles.camListSpots} data-testid="cam-list-spots">
        {spots.map((spot) => (
          <div
            className={styles.camListSpotsRef}
            key={spot._id}
            ref={(spotEl) => {
              spotRefs.current[spot._id] = spotEl;
            }}
          >
            <CamListItem
              spot={spot}
              units={units}
              showPremiumCTA={
                paywallRedesign === 'off' && showPremiumCamCTA({ spot, hasPremiumCamPermissions })
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CamList;
